import { useEffect, useState } from "react";
import { Menu, Phone, X } from "lucide-react";

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "About Us", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Our Work", href: "#gallery" },
  { label: "Maintenance Plans", href: "#plans" },
  { label: "Free Estimate", href: "#estimate" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar({ phone }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-nav"
      className={`sticky top-0 z-50 bg-white/95 backdrop-blur-md border-t-4 border-brand border-b border-border transition-shadow duration-200 ${scrolled ? "shadow-sm" : ""}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
        <a href="#home" data-testid="nav-logo-link" className="flex items-center shrink-0">
          <img
            src="/assets/logo.png"
            alt="Somoza's Landscaping, Lawn Care & Tree Service logo"
            className="h-12 md:h-16 w-auto"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-6" aria-label="Main navigation">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-ink font-medium hover:text-brand transition-colors duration-200 text-xs xl:text-sm uppercase tracking-wide"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${(phone || "214-576-8224").replace(/[^0-9+]/g, "")}`}
            data-testid="nav-call-button"
            className="hidden md:inline-flex items-center gap-2 bg-brand text-white font-semibold rounded-full px-5 py-2.5 hover:bg-brand-hover transition-colors duration-200 shadow-sm text-sm"
          >
            <Phone className="w-4 h-4" aria-hidden="true" />
            {phone || "214-576-8224"}
          </a>
          <button
            type="button"
            data-testid="nav-mobile-menu-button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-ink hover:text-brand transition-colors"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-white border-t border-border"
          aria-label="Mobile navigation"
        >
          <div className="px-4 py-3 flex flex-col">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-mobile-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setOpen(false)}
                className="py-3 border-b border-border last:border-0 text-ink font-medium hover:text-brand transition-colors text-sm uppercase tracking-wide"
              >
                {link.label}
              </a>
            ))}
            <a
              href={`tel:${(phone || "214-576-8224").replace(/[^0-9+]/g, "")}`}
              data-testid="nav-mobile-call-button"
              className="mt-3 mb-2 inline-flex items-center justify-center gap-2 bg-brand text-white font-semibold rounded-full px-5 py-3 hover:bg-brand-hover transition-colors"
            >
              <Phone className="w-4 h-4" aria-hidden="true" />
              Call {phone || "214-576-8224"}
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
